import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  getOrganizationBySlug, 
  getApartmentsPublic,
  createDocument,
  updateDocument,
  sendEmail,
  sendSMS,
  Organization,
  Apartment
} from '../lib/api';
import { 
  Buildings,
  User,
  Plus,
  Trash,
  EnvelopeSimple,
  Phone,
  House,
  CheckCircle,
  SpinnerGap,
  Receipt
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

interface ResidentInfo {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  is_invoice_recipient?: boolean;
}

interface PendingUpdate {
  id: string;
  organization_id: string;
  apartment_id: string;
  residents: ResidentInfo[];
  verification_token: string;
  access_key?: string; // Used for authenticating verification requests
  email_verified: boolean;
  created_at: string;
  expires_at: string;
}

// Success screen with auto-redirect
function SuccessScreen({ 
  portalSettings, 
  organization, 
  onReset 
}: { 
  portalSettings: { header_image?: string; logo?: string };
  organization: Organization | null;
  onReset: () => void;
}) {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onReset]);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-slate-50">
      {portalSettings.header_image && (
        <div className="h-32 sm:h-40 md:h-48 relative">
          <img 
            src={portalSettings.header_image} 
            alt={organization?.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      )}

      <div className={`max-w-sm sm:max-w-md mx-auto px-4 py-6 sm:py-8 ${portalSettings.header_image ? '-mt-12 sm:-mt-16' : ''} relative z-10`}>
        <Card className="shadow-lg">
          <CardContent className="pt-6 sm:pt-8 pb-6 px-4 sm:px-6 text-center">
            {portalSettings.logo && (
              <img 
                src={portalSettings.logo} 
                alt="Logo" 
                className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-lg bg-white shadow object-contain p-1.5 sm:p-2"
              />
            )}
            <CheckCircle size={48} className="mx-auto text-green-500 mb-3 sm:mb-4 sm:w-16 sm:h-16" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
              Uppgifterna har registrerats!
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
              Tack! Dina uppgifter har registrerats och kommer att behandlas av styrelsen.
            </p>
            <p className="text-xs text-slate-400 mb-4">
              Återgår till startsidan om {countdown} sekunder...
            </p>
            <Button 
              variant="outline" 
              className="h-10 text-sm"
              onClick={onReset}
            >
              Uppdatera igen
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function MemberUpdatePage() {
  const { slug } = useParams<{ slug: string }>();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Email verification state
  const [awaitingEmailCode, setAwaitingEmailCode] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [expectedEmailCode, setExpectedEmailCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [pendingUpdateId, setPendingUpdateId] = useState<string | null>(null);
  
  // Phone verification state
  const [askPhoneVerification, setAskPhoneVerification] = useState(false);
  const [awaitingPhoneCode, setAwaitingPhoneCode] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const [expectedPhoneCode, setExpectedPhoneCode] = useState('');
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [currentPhoneIndex, setCurrentPhoneIndex] = useState(0);
  const [phonesVerified, setPhonesVerified] = useState<string[]>([]);
  const [residentsWithPhone, setResidentsWithPhone] = useState<ResidentInfo[]>([]);

  // Form state
  const [selectedApartment, setSelectedApartment] = useState('');
  const [residents, setResidents] = useState<ResidentInfo[]>([
    { id: crypto.randomUUID(), first_name: '', last_name: '', email: '', phone: '' }
  ]);

  // Force mobile viewport on mount
  useEffect(() => {
    // Ensure viewport meta tag exists and is correct
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      document.head.appendChild(viewport);
    }
    viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes');
    
    // Force body styles for mobile
    document.body.style.minWidth = '0';
    document.body.style.width = '100%';
    document.body.style.maxWidth = '100vw';
    document.body.style.overflowX = 'hidden';
    
    // Add mobile class to html element
    document.documentElement.classList.add('mobile-portal');
    
    return () => {
      document.documentElement.classList.remove('mobile-portal');
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      const org = await getOrganizationBySlug(slug);
      if (!org) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setOrganization(org);

      // Load apartments for this organization (public endpoint)
      console.log('MemberUpdatePage - loading apartments for org:', org.id);
      const aptsResponse = await getApartmentsPublic(org.id);
      console.log('MemberUpdatePage - apartments response:', aptsResponse);
      if (aptsResponse.data) {
        // Alphanumeric sort: 1A, 1B, 2, 10, etc.
        const sortedApts = aptsResponse.data.sort((a, b) => {
          const numA = a.apartment_number || '';
          const numB = b.apartment_number || '';
          return numA.localeCompare(numB, 'sv', { numeric: true });
        });
        console.log('MemberUpdatePage - sorted apartments:', sortedApts.map(a => a.apartment_number));
        setApartments(sortedApts);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // Verify email code
  const handleVerifyEmailCode = async () => {
    if (emailCode.length !== 6) {
      toast.error('Ange en 6-siffrig kod');
      return;
    }

    if (emailCode !== expectedEmailCode) {
      toast.error('Fel kod. Kontrollera din e-post och försök igen.');
      return;
    }

    setVerifyingEmail(true);
    
    try {
      // Try to update the database
      if (pendingUpdateId) {
        try {
          await updateDocument('member_updates', pendingUpdateId, {
            email_verified: true,
            verified_at: new Date().toISOString()
          });
        } catch (updateErr) {
          console.log('Could not update DB:', updateErr);
        }
      }
      
      setEmailVerified(true);
      setAwaitingEmailCode(false);
      
      // Check if there are phone numbers to verify
      const withPhone = residents.filter(r => r.phone && r.phone.trim());
      if (withPhone.length > 0) {
        setResidentsWithPhone(withPhone);
        setAskPhoneVerification(true);
      } else {
        // No phone numbers, complete directly
        completeRegistration();
      }
    } catch (error) {
      console.error('Email verification failed:', error);
      toast.error('Något gick fel. Försök igen.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  // Send SMS verification code
  const sendPhoneCode = async (resident: ResidentInfo) => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setExpectedPhoneCode(code);
    
    // Format phone number for Swedish format
    let phone = resident.phone.replace(/\D/g, '');
    if (phone.startsWith('0')) {
      phone = '46' + phone.slice(1);
    }
    if (!phone.startsWith('46')) {
      phone = '46' + phone;
    }
    const formattedPhone = '+' + phone;
    
    try {
      await sendSMS(formattedPhone, `Din verifieringskod för ${organization?.name}: ${code}`);
      toast.success(`SMS skickat till ${resident.phone}`);
      setAwaitingPhoneCode(true);
    } catch (error) {
      console.error('Failed to send SMS:', error);
      toast.error('Kunde inte skicka SMS. Försök igen eller hoppa över.');
    }
  };

  // Verify phone code
  const handleVerifyPhoneCode = async () => {
    if (phoneCode.length !== 6) {
      toast.error('Ange en 6-siffrig kod');
      return;
    }

    if (phoneCode !== expectedPhoneCode) {
      toast.error('Fel kod. Kontrollera ditt SMS och försök igen.');
      return;
    }

    setVerifyingPhone(true);
    
    try {
      const currentResident = residentsWithPhone[currentPhoneIndex];
      const newPhonesVerified = [...phonesVerified, currentResident.phone];
      setPhonesVerified(newPhonesVerified);
      setPhoneCode('');
      
      // Check if there are more phones to verify
      if (currentPhoneIndex < residentsWithPhone.length - 1) {
        setCurrentPhoneIndex(currentPhoneIndex + 1);
        setAwaitingPhoneCode(false); // Go back to "send code" for next phone
        toast.success('Telefonnummer verifierat!');
        setVerifyingPhone(false);
      } else {
        // All phones verified - go directly to success
        try {
          if (pendingUpdateId) {
            await updateDocument('member_updates', pendingUpdateId, {
              phone_verified: true,
              status: 'pending_approval'
            });
          }
        } catch (updateErr) {
          console.log('Could not update DB:', updateErr);
        }
        // Don't reset awaitingPhoneCode - go straight to success
        await completeRegistration();
      }
    } catch (error) {
      console.error('Phone verification failed:', error);
      toast.error('Något gick fel. Försök igen.');
      setVerifyingPhone(false);
    }
  };

  // Complete the registration
  const completeRegistration = async () => {
    try {
      if (pendingUpdateId) {
        await updateDocument('member_updates', pendingUpdateId, {
          status: 'pending_approval',
          completed_at: new Date().toISOString()
        });
      }
    } catch (updateErr) {
      console.log('Could not update DB:', updateErr);
    }
    
    setAskPhoneVerification(false);
    setAwaitingPhoneCode(false);
    setSubmitted(true);
    toast.success('Tack! Dina uppgifter har registrerats.');
  };

  // Skip phone verification
  const skipPhoneVerification = async () => {
    try {
      if (pendingUpdateId) {
        await updateDocument('member_updates', pendingUpdateId, {
          phone_verified: false,
          status: 'pending_approval'
        });
      }
    } catch (updateErr) {
      console.log('Could not update DB:', updateErr);
    }
    completeRegistration();
  };

  const addResident = () => {
    setResidents([...residents, { id: crypto.randomUUID(), first_name: '', last_name: '', email: '', phone: '' }]);
  };

  const removeResident = (id: string) => {
    if (residents.length > 1) {
      setResidents(residents.filter(r => r.id !== id));
    }
  };

  const updateResident = (id: string, field: keyof ResidentInfo, value: string | boolean) => {
    setResidents(residents.map(r => 
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedApartment) {
      toast.error('Välj en lägenhet');
      return;
    }

    const validResidents = residents.filter(r => r.first_name && r.last_name && r.email);
    if (validResidents.length === 0) {
      toast.error('Fyll i minst en person med namn och e-post');
      return;
    }

    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const resident of validResidents) {
      if (!emailRegex.test(resident.email)) {
        toast.error(`Ogiltig e-postadress: ${resident.email}`);
        return;
      }
    }

    setSubmitting(true);

    try {
      // Generate 6-digit verification code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      const verificationToken = crypto.randomUUID();
      const expiresAt = new Date(Date.now() + 30 * 60 * 1000).toISOString(); // 30 minutes

      const docId = crypto.randomUUID();
      const updateData = {
        id: docId,
        organization_id: organization?.id,
        apartment_id: selectedApartment,
        residents: validResidents,
        verification_token: verificationToken,
        verification_code: code,
        email_verified: false,
        status: 'pending_verification',
        created_at: new Date().toISOString(),
        expires_at: expiresAt
      };

      await createDocument('member_updates', updateData);
      const apartment = apartments.find(a => a.id === selectedApartment);

      // Email with verification code
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            ${organization?.portal_settings?.logo ? 
              `<img src="${organization.portal_settings.logo}" alt="${organization?.name}" style="max-height: 60px; margin-bottom: 15px;">` : 
              ''}
            <h1 style="color: #1e293b; margin: 0;">${organization?.name}</h1>
          </div>
          
          <div style="background: #f8fafc; border-radius: 8px; padding: 24px; margin-bottom: 20px;">
            <h2 style="color: #1e293b; margin-top: 0;">Din verifieringskod</h2>
            <p style="color: #64748b;">
              Du har begärt att uppdatera kontaktinformation för lägenhet <strong>${apartment?.apartment_number}</strong>.
            </p>
            <p style="color: #64748b;">
              Ange denna kod på webbsidan för att bekräfta din e-postadress:
            </p>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <div style="display: inline-block; background: #1e293b; color: white; padding: 20px 40px; 
                        border-radius: 12px; font-size: 32px; font-weight: bold; letter-spacing: 8px;">
              ${code}
            </div>
          </div>
          
          <p style="color: #94a3b8; font-size: 12px; text-align: center;">
            Koden är giltig i 30 minuter. Om du inte har begärt denna uppdatering kan du ignorera detta mail.
          </p>
        </div>
      `;

      // Send to all email addresses
      const emails = validResidents.map(r => r.email);
      await sendEmail(emails, `Din verifieringskod: ${code} - ${organization?.name}`, emailHtml, { fromName: organization?.name || 'Förvaltningen' });

      // Store code locally and show code input
      setExpectedEmailCode(code);
      setPendingUpdateId(docId);
      setAwaitingEmailCode(true);
      toast.success('Verifieringskod har skickats till din e-post!');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('Något gick fel. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-slate-50">
        <SpinnerGap size={40} className="animate-spin text-primary" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-slate-50 px-4 py-6 sm:p-4">
        <Card className="max-w-sm sm:max-w-md w-full shadow-lg">
          <CardContent className="pt-8 pb-8 sm:pt-6 text-center px-6">
            <Buildings size={48} className="mx-auto text-slate-300 mb-4 sm:w-16 sm:h-16" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Föreningen hittades inte</h1>
            <p className="text-sm sm:text-base text-slate-600">
              Kontrollera att du har rätt adress eller kontakta din bostadsrättsförening.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const portalSettings = organization?.portal_settings || {};

  // Email code verification state
  if (awaitingEmailCode && !emailVerified) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-50">
        {/* Header with image */}
        {portalSettings.header_image && (
          <div className="h-32 sm:h-40 md:h-48 relative">
            <img 
              src={portalSettings.header_image} 
              alt={organization?.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        )}

        <div className={`max-w-sm sm:max-w-md mx-auto px-4 py-6 sm:py-8 ${portalSettings.header_image ? '-mt-12 sm:-mt-16' : ''} relative z-10`}>
          <Card className="shadow-lg">
            <CardHeader className="text-center px-4 sm:px-6 pt-6 pb-2">
              {portalSettings.logo && (
                <img 
                  src={portalSettings.logo} 
                  alt="Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-lg bg-white shadow object-contain p-1.5 sm:p-2"
                />
              )}
              <EnvelopeSimple size={32} className="mx-auto text-primary mb-2" />
              <CardTitle className="text-lg sm:text-xl">Verifiera e-post</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-6">
              <p className="text-center text-sm sm:text-base text-slate-600">
                Vi har skickat en 6-siffrig kod till din e-post. Ange koden nedan för att bekräfta.
              </p>
              
              <div className="space-y-3">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl sm:text-3xl tracking-[0.5em] font-mono h-14 sm:h-16"
                  maxLength={6}
                  autoFocus
                />
                
                <Button 
                  onClick={handleVerifyEmailCode}
                  disabled={emailCode.length !== 6 || verifyingEmail}
                  className="w-full h-12 sm:h-11 text-base sm:text-sm"
                >
                  {verifyingEmail ? (
                    <>
                      <SpinnerGap size={18} className="mr-2 animate-spin" />
                      Verifierar...
                    </>
                  ) : (
                    'Bekräfta e-post'
                  )}
                </Button>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 sm:p-4 text-center">
                <p className="text-xs sm:text-sm text-slate-500">
                  Hittar du inte mailet? Kontrollera skräpposten.
                </p>
                <button 
                  onClick={() => {
                    setAwaitingEmailCode(false);
                    setEmailCode('');
                  }}
                  className="text-xs sm:text-sm text-primary hover:underline mt-2"
                >
                  Skicka ny kod
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Ask if user wants to verify phone
  if (askPhoneVerification && !awaitingPhoneCode && !submitted) {
    const currentResident = residentsWithPhone[currentPhoneIndex];
    
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-50">
        {portalSettings.header_image && (
          <div className="h-32 sm:h-40 md:h-48 relative">
            <img 
              src={portalSettings.header_image} 
              alt={organization?.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        )}

        <div className={`max-w-sm sm:max-w-md mx-auto px-4 py-6 sm:py-8 ${portalSettings.header_image ? '-mt-12 sm:-mt-16' : ''} relative z-10`}>
          <Card className="shadow-lg">
            <CardHeader className="text-center px-4 sm:px-6 pt-6 pb-2">
              {portalSettings.logo && (
                <img 
                  src={portalSettings.logo} 
                  alt="Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-lg bg-white shadow object-contain p-1.5 sm:p-2"
                />
              )}
              <CheckCircle size={32} className="mx-auto text-green-500 mb-2" />
              <CardTitle className="text-lg sm:text-xl">E-post verifierad!</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-6">
              <p className="text-center text-sm sm:text-base text-slate-600">
                Vill du även verifiera ditt telefonnummer för ökad säkerhet?
              </p>
              
              <div className="bg-slate-50 rounded-lg p-3 sm:p-4 space-y-2">
                <p className="text-xs sm:text-sm font-medium text-slate-700">
                  {residentsWithPhone.length === 1 ? 'Telefonnummer:' : `Telefonnummer (${currentPhoneIndex + 1} av ${residentsWithPhone.length}):`}
                </p>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone size={16} className="text-slate-400" />
                  <span className="font-medium">{currentResident?.first_name} {currentResident?.last_name}</span>
                  <span className="text-slate-400">—</span>
                  <span>{currentResident?.phone}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Button 
                  onClick={() => sendPhoneCode(currentResident)}
                  className="w-full h-11 sm:h-10"
                >
                  <Phone size={18} className="mr-2" />
                  Skicka SMS-kod
                </Button>
                <Button 
                  variant="ghost"
                  onClick={skipPhoneVerification}
                  className="w-full h-10 text-slate-500"
                >
                  Hoppa över
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Phone code verification
  if (awaitingPhoneCode && !submitted) {
    const currentResident = residentsWithPhone[currentPhoneIndex];
    
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-50">
        {portalSettings.header_image && (
          <div className="h-32 sm:h-40 md:h-48 relative">
            <img 
              src={portalSettings.header_image} 
              alt={organization?.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        )}

        <div className={`max-w-sm sm:max-w-md mx-auto px-4 py-6 sm:py-8 ${portalSettings.header_image ? '-mt-12 sm:-mt-16' : ''} relative z-10`}>
          <Card className="shadow-lg">
            <CardHeader className="text-center px-4 sm:px-6 pt-6 pb-2">
              {portalSettings.logo && (
                <img 
                  src={portalSettings.logo} 
                  alt="Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-lg bg-white shadow object-contain p-1.5 sm:p-2"
                />
              )}
              <Phone size={32} className="mx-auto text-primary mb-2" />
              <CardTitle className="text-lg sm:text-xl">Verifiera telefon</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-6">
              <p className="text-center text-sm sm:text-base text-slate-600">
                Vi har skickat en kod till <strong>{currentResident?.phone}</strong>
              </p>
              
              {/* Progress indicator for multiple phones */}
              {residentsWithPhone.length > 1 && (
                <div className="flex justify-center gap-1">
                  {residentsWithPhone.map((_, idx) => (
                    <div 
                      key={idx}
                      className={`w-2 h-2 rounded-full ${
                        idx < currentPhoneIndex ? 'bg-green-500' : 
                        idx === currentPhoneIndex ? 'bg-primary' : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>
              )}
              
              <div className="space-y-3">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl sm:text-3xl tracking-[0.5em] font-mono h-14 sm:h-16"
                  maxLength={6}
                  autoFocus
                />
                
                <Button 
                  onClick={handleVerifyPhoneCode}
                  disabled={phoneCode.length !== 6 || verifyingPhone}
                  className="w-full h-12 sm:h-11 text-base sm:text-sm"
                >
                  {verifyingPhone ? (
                    <>
                      <SpinnerGap size={18} className="mr-2 animate-spin" />
                      Verifierar...
                    </>
                  ) : (
                    'Bekräfta'
                  )}
                </Button>
              </div>

              <div className="flex justify-between text-xs sm:text-sm">
                <button 
                  onClick={() => sendPhoneCode(currentResident)}
                  className="text-primary hover:underline"
                >
                  Skicka ny kod
                </button>
                <button 
                  onClick={skipPhoneVerification}
                  className="text-slate-500 hover:underline"
                >
                  Hoppa över
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return <SuccessScreen 
      portalSettings={portalSettings} 
      organization={organization}
      onReset={() => {
        setSubmitted(false);
        setAwaitingEmailCode(false);
        setEmailCode('');
        setEmailVerified(false);
        setAskPhoneVerification(false);
        setAwaitingPhoneCode(false);
        setPhoneCode('');
        setPhonesVerified([]);
        setCurrentPhoneIndex(0);
        setResidentsWithPhone([]);
        setResidents([{ id: crypto.randomUUID(), first_name: '', last_name: '', email: '', phone: '' }]);
        setSelectedApartment('');
      }}
    />;
  }

  // Main form
  return (
    <div className="min-h-screen min-h-[100dvh] bg-slate-50 portal-mobile">
      {/* Header with image */}
      {portalSettings.header_image ? (
        <div className="h-36 sm:h-48 md:h-64 relative">
          <img 
            src={portalSettings.header_image} 
            alt={organization?.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <div className="w-full sm:max-w-lg mx-auto flex items-end gap-3 sm:gap-4 px-4 sm:px-0">
              {portalSettings.logo && (
                <img 
                  src={portalSettings.logo} 
                  alt="Logo" 
                  className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg bg-white shadow-lg object-contain p-1.5 sm:p-2"
                />
              )}
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg truncate">
                  {organization?.name}
                </h1>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-primary text-white p-4 sm:p-6">
          <div className="w-full sm:max-w-lg mx-auto flex items-center gap-3 sm:gap-4 px-4 sm:px-0">
            {portalSettings.logo && (
              <img 
                src={portalSettings.logo} 
                alt="Logo" 
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-white shadow object-contain p-1.5 sm:p-2"
              />
            )}
            <h1 className="text-xl sm:text-2xl font-bold truncate">{organization?.name}</h1>
          </div>
        </div>
      )}

      <div className="w-full sm:max-w-lg mx-auto px-4 sm:px-4 py-4 sm:py-8">
        <Card className="shadow-lg">
          <CardHeader className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2.5 text-xl sm:text-xl">
              <User size={24} className="text-primary sm:w-6 sm:h-6" />
              Uppdatera kontaktuppgifter
            </CardTitle>
            <p className="text-sm sm:text-sm text-slate-500 mt-2 sm:mt-2 leading-relaxed">
              Fyll i kontaktuppgifterna för din lägenhet.<br />
              Du kan lägga till flera personer med + tecknet.
            </p>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-5 sm:pb-6">
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              {/* Apartment selection */}
              <div className="space-y-2 sm:space-y-2">
                <Label className="flex items-center gap-2 text-sm sm:text-sm font-medium">
                  <House size={16} className="sm:w-4 sm:h-4" />
                  Välj lägenhet *
                </Label>
                <Select value={selectedApartment} onValueChange={setSelectedApartment}>
                  <SelectTrigger data-testid="apartment-select" className="h-12 sm:h-10 text-base sm:text-sm">
                    <SelectValue placeholder="Välj din lägenhet..." />
                  </SelectTrigger>
                  <SelectContent>
                    {apartments.map((apt) => (
                      <SelectItem key={apt.id} value={apt.id} className="text-base sm:text-sm py-3 sm:py-2">
                        Lägenhet {apt.apartment_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Residents */}
              <div className="space-y-4 sm:space-y-4">
                <Label className="text-sm sm:text-sm font-medium">Boende i lägenheten</Label>
                
                {residents.map((resident, index) => (
                  <div 
                    key={resident.id} 
                    className="border border-slate-200 rounded-lg p-4 sm:p-4 space-y-4 sm:space-y-3 bg-slate-50"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm sm:text-sm font-medium text-slate-700">
                        Person {index + 1}
                      </span>
                      {residents.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeResident(resident.id)}
                          className="text-red-500 hover:text-red-700 p-2 -mr-1 rounded-md hover:bg-red-50 active:bg-red-100"
                        >
                          <Trash size={18} className="sm:w-[16px] sm:h-[16px]" />
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-4 sm:space-y-3">
                      <div className="grid grid-cols-2 gap-3 sm:gap-2">
                        <div>
                          <Label className="text-sm sm:text-xs mb-1.5 sm:mb-1 block font-medium">Förnamn *</Label>
                          <Input
                            value={resident.first_name}
                            onChange={(e) => updateResident(resident.id, 'first_name', e.target.value)}
                            placeholder="Anna"
                            data-testid={`resident-first-name-${index}`}
                            className="h-12 sm:h-10 text-base sm:text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-sm sm:text-xs mb-1.5 sm:mb-1 block font-medium">Efternamn *</Label>
                          <Input
                            value={resident.last_name}
                            onChange={(e) => updateResident(resident.id, 'last_name', e.target.value)}
                            placeholder="Andersson"
                            data-testid={`resident-last-name-${index}`}
                            className="h-12 sm:h-10 text-base sm:text-sm"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm sm:text-xs flex items-center gap-1.5 mb-1.5 sm:mb-1 font-medium">
                          <EnvelopeSimple size={14} className="sm:w-3 sm:h-3" />
                          E-post *
                        </Label>
                        <Input
                          type="email"
                          inputMode="email"
                          value={resident.email}
                          onChange={(e) => updateResident(resident.id, 'email', e.target.value)}
                          placeholder="anna@example.com"
                          data-testid={`resident-email-${index}`}
                          className="h-12 sm:h-10 text-base sm:text-sm"
                        />
                        <div className="flex items-start gap-3 mt-3 sm:mt-2 p-3 sm:p-2 bg-white rounded-md border border-slate-100">
                          <Checkbox
                            id={`invoice-${resident.id}`}
                            checked={resident.is_invoice_recipient || false}
                            onCheckedChange={(checked) => {
                              // Om denna bockas i, avbocka alla andra
                              if (checked) {
                                setResidents(residents.map(r => ({
                                  ...r,
                                  is_invoice_recipient: r.id === resident.id
                                })));
                              } else {
                                updateResident(resident.id, 'is_invoice_recipient', false);
                              }
                            }}
                            data-testid={`invoice-recipient-${index}`}
                            className="mt-0.5 h-5 w-5 sm:h-4 sm:w-4"
                          />
                          <label 
                            htmlFor={`invoice-${resident.id}`}
                            className="text-sm sm:text-xs text-slate-600 cursor-pointer leading-snug"
                          >
                            <span className="flex items-center gap-1.5">
                              <Receipt size={14} className="text-slate-400 sm:w-3 sm:h-3" />
                              Skicka faktura för medlemsavgift hit
                            </span>
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm sm:text-xs flex items-center gap-1.5 mb-1.5 sm:mb-1 font-medium">
                          <Phone size={14} className="sm:w-3 sm:h-3" />
                          Mobilnummer
                        </Label>
                        <Input
                          type="tel"
                          inputMode="tel"
                          value={resident.phone}
                          onChange={(e) => updateResident(resident.id, 'phone', formatPhoneNumber(e.target.value))}
                          placeholder="070-123 45 67"
                          data-testid={`resident-phone-${index}`}
                          className="h-12 sm:h-10 text-base sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addResident}
                  className="w-full h-12 sm:h-10 text-base sm:text-sm"
                  data-testid="add-resident-button"
                >
                  <Plus size={18} className="mr-2 sm:w-4 sm:h-4" />
                  Lägg till person
                </Button>
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 sm:h-11 text-base sm:text-sm font-semibold" 
                disabled={submitting}
                data-testid="submit-button"
              >
                {submitting ? (
                  <>
                    <SpinnerGap size={20} className="mr-2 animate-spin sm:w-[18px] sm:h-[18px]" />
                    Skickar...
                  </>
                ) : (
                  'Bekräfta uppgifter'
                )}
              </Button>

              <p className="text-xs sm:text-xs text-slate-400 text-center leading-relaxed">
                Ett bekräftelsemail skickas till alla angivna e-postadresser.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
